import type { NativeSelectProps } from '@_/ui';
import { useFocusWithin } from '@mantine/hooks';
import { times } from 'es-toolkit/compat';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { dateTimeCommonI18n } from '@_/i18n-common';
import { InputContainer, NativeSelect, NativeSelectOption } from '@_/ui';
import { getDaysInMonth, getI18nMonths } from '@_/utils';

export interface DobSelectProps extends Pick<
  NativeSelectProps,
  'cornerText' | 'disabled' | 'error' | 'helpText' | 'label' | 'onChange' | 'value'
> {
  onBlur?: () => void;
}

export function DobSelect({
  cornerText,
  disabled,
  error,
  helpText,
  label,
  onBlur,
  onChange,
  value
}: DobSelectProps) {
  const { locale } = useIntl();
  const { ref } = useFocusWithin({ onBlur });

  const [year, month, day] = useMemo(() => {
    const [y, m, d] = (value ?? '').split('/');
    return [y, m, d].map(toInt);
  }, [value]);

  const years = useMemo(() => {
    const currentYear = DateTime.local().year;
    return times(100, i => currentYear - i);
  }, []);
  const months = useMemo(() => getI18nMonths(locale), [locale]);
  const numDays = year && month ? getDaysInMonth(year, month) : 0;
  const days = useMemo(() => (numDays ? times(numDays, i => i + 1) : []), [numDays]);

  const handleChange = (field: 'day' | 'month' | 'year', val: string | null) => {
    const date = {
      year: field === 'year' ? toInt(val) : year,
      month: field === 'month' ? toInt(val) : month,
      day: field === 'day' ? toInt(val) : day
    };

    const daysInMonth = date.year && date.month ? getDaysInMonth(date.year, date.month) : undefined;

    if (date.day && daysInMonth && date.day > daysInMonth) {
      date.day = null;
    }

    const newValue = `${date.year ?? ''}/${date.month ?? ''}/${date.day ?? ''}`;

    onChange?.(newValue === '//' ? null : newValue);
  };

  return (
    <InputContainer cornerText={cornerText} error={error} helpText={helpText} label={label}>
      <div ref={ref} className="grid grid-cols-3 gap-x-3">
        <NativeSelect
          disabled={disabled}
          error={!!error}
          onChange={v => handleChange('year', v)}
          value={year?.toString() || null}
        >
          <NativeSelectOption disabled value="">
            <FormattedMessage {...dateTimeCommonI18n.year} />
          </NativeSelectOption>
          {years.map(y => (
            <NativeSelectOption key={y} value={y}>
              {y}
            </NativeSelectOption>
          ))}
        </NativeSelect>

        <NativeSelect
          disabled={disabled}
          error={!!error}
          onChange={v => handleChange('month', v)}
          value={month?.toString() || null}
        >
          <NativeSelectOption disabled value="">
            <FormattedMessage {...dateTimeCommonI18n.month} />
          </NativeSelectOption>
          {months.map((m, index) => (
            <NativeSelectOption key={m} value={index + 1}>
              {m}
            </NativeSelectOption>
          ))}
        </NativeSelect>

        <NativeSelect
          disabled={disabled || !month || !year}
          error={!!error}
          onChange={v => handleChange('day', v)}
          value={day?.toString() || null}
        >
          <NativeSelectOption disabled value="">
            <FormattedMessage {...dateTimeCommonI18n.day} />
          </NativeSelectOption>
          {days.map(d => (
            <NativeSelectOption key={d} value={d}>
              {d}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
    </InputContainer>
  );
}

function toInt(part?: string | null): number | null {
  const parsed = parseInt(part ?? '', 10);
  return Number.isNaN(parsed) ? null : parsed;
}
